import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Target, CheckCircle2, Settings2 } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

export default function WeeklyGoal() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ target_attempts: 15, target_correct: 10, target_subjects: 2 });
  const [celebrated, setCelebrated] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/goals/current");
      setData(data);
      setForm({
        target_attempts: data.target.attempts,
        target_correct: data.target.correct,
        target_subjects: data.target.subjects,
      });
      if (data.completed) {
        setCelebrated((wasCelebrated) => {
          if (!wasCelebrated) {
            confetti({ particleCount: 200, spread: 100, colors: ["#FF5E5B", "#FFD166", "#06D6A0", "#4F98E8"] });
            toast.success("Weekly goal complete! 🎉", { description: "Amazing work — new goal starts Monday." });
          }
          return true;
        });
      }
    } catch (err) {
      console.error("weekly goal load failed:", err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const { data } = await api.post("/goals/set", form);
      setData(data);
      setEditing(false);
      toast.success("Weekly goal updated");
    } catch { toast.error("Could not save"); }
  };

  if (!data) return null;
  const p = data.progress; const t = data.target;
  return (
    <div data-testid="weekly-goal" className={`nb-card p-6 ${data.completed ? "bg-[var(--mint)]" : "bg-white"}`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <span className="nb-badge nb-tag-sky"><Target className="w-3.5 h-3.5" /> Weekly goal</span>
          <h2 className="text-2xl font-extrabold mt-2">
            {data.completed ? <>Goal smashed <CheckCircle2 className="w-6 h-6 inline-block" /></> : "This week's target"}
          </h2>
          <p className="text-sm text-neutral-700">Week of {data.week_start}</p>
        </div>
        <div className="text-right">
          <div className="text-5xl font-extrabold">{data.percent}%</div>
          <button data-testid="edit-goal-btn" onClick={() => setEditing((e) => !e)} className="nb-btn nb-btn-secondary text-xs px-3 py-1 mt-1">
            <Settings2 className="w-3 h-3" /> {editing ? "Cancel" : "Edit"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-4">
        <MetricBar label="Questions" value={p.attempts} target={t.attempts} />
        <MetricBar label="Correct" value={p.correct} target={t.correct} />
        <MetricBar label="Subjects" value={p.subjects_touched} target={t.subjects} />
      </div>

      {editing && (
        <div className="mt-4 grid grid-cols-3 gap-2">
          <NumberInput label="Attempts" testid="goal-attempts" value={form.target_attempts} onChange={(v) => setForm({ ...form, target_attempts: v })} min={1} max={200} />
          <NumberInput label="Correct" testid="goal-correct" value={form.target_correct} onChange={(v) => setForm({ ...form, target_correct: v })} min={1} max={200} />
          <NumberInput label="Subjects" testid="goal-subjects" value={form.target_subjects} onChange={(v) => setForm({ ...form, target_subjects: v })} min={1} max={3} />
          <div className="col-span-3">
            <button data-testid="save-goal-btn" onClick={save} className="nb-btn w-full">Save goal</button>
          </div>
        </div>
      )}

      {!editing && !data.completed && (
        <Link data-testid="goal-cta-practice" to="/practice" className="nb-btn nb-btn-accent mt-4 inline-flex">Keep going</Link>
      )}
    </div>
  );
}

function MetricBar({ label, value, target }) {
  const pct = Math.min(100, Math.round((value / Math.max(target, 1)) * 100));
  return (
    <div className="p-3 border-2 border-black rounded-xl bg-white">
      <div className="text-xs uppercase font-bold text-neutral-500">{label}</div>
      <div className="text-2xl font-extrabold">{value}<span className="text-sm text-neutral-500"> / {target}</span></div>
      <div className="h-2 mt-1 bg-neutral-200 rounded-full border-2 border-black overflow-hidden">
        <div className={`h-full ${pct >= 100 ? "bg-[var(--mint)]" : "bg-[var(--coral)]"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function NumberInput({ label, value, onChange, min, max, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase">{label}</span>
      <input data-testid={testid} type="number" min={min} max={max} value={value} onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value) || min)))} className="nb-input mt-1" />
    </label>
  );
}
